import { useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import AstrologerCard from './AstrologerCard'
import { decorateAstrologer, skillEmoji } from './astrologersData'
import { useRevealClass } from '../../../hooks/useRevealClass'
import './AstrologersSection.css'
import { appUrl } from '@astro/config'

// Maharaj Ji's own account is featured separately, so keep it out of the
// general astrologer panel. Match by email when the payload carries it;
// the public /astrologers/filter list omits email, so fall back to the id.
const EXCLUDED_EMAILS = ['rjn.bharti333@gmail.com']
const EXCLUDED_ID_FALLBACK = 15

function isExcludedAstrologer(record) {
  const email = record?.email ?? record?.user?.email
  if (email) return EXCLUDED_EMAILS.includes(String(email).toLowerCase())
  return record?.id === EXCLUDED_ID_FALLBACK
}

/** Debounce the search box so we don't hit the API on every keystroke. */
function useDebouncedValue(value, delayMs = 350) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

function SkeletonCard() {
  return (
    <div className="rounded-3xl overflow-hidden animate-pulse" style={{ background: '#FFF', border: '1px solid rgba(200,146,42,.12)' }}>
      <div style={{ aspectRatio: '4/4.4', background: 'rgba(200,146,42,.10)' }} />
      <div className="p-5 space-y-3">
        <div className="h-4 w-2/3 rounded" style={{ background: 'rgba(200,146,42,.14)' }} />
        <div className="h-2.5 w-1/2 rounded" style={{ background: 'rgba(200,146,42,.10)' }} />
        <div className="grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 rounded-xl" style={{ background: 'rgba(200,146,42,.08)' }} />
          ))}
        </div>
        <div className="h-8 rounded-xl" style={{ background: 'rgba(200,146,42,.10)' }} />
      </div>
    </div>
  )
}

export default function AstrologersSection() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [revealedIds, setRevealedIds] = useState(() => new Set())
  const [gridInView, setGridInView] = useState(false)
  const gridRef = useRef(null)

  const headerReveal = useRevealClass()
  const filterRowReveal = useRevealClass()
  const ctaStripReveal = useRevealClass()

  const debouncedSearch = useDebouncedValue(search)

  // Filter tabs come from the live skills lookup (they rarely change).
  const { data: skills } = useQuery({
    queryKey: ['skills'],
    queryFn: () => api.lookups.skills(),
    staleTime: Infinity,
  })

  const filterTabs = useMemo(
    () => [
      { key: 'all', label: 'All' },
      ...(skills ?? []).map((s) => ({ key: s.name, label: `${skillEmoji(s.slug)} ${s.name}` })),
    ],
    [skills],
  )

  // /astrologers/filter is the richest read (includes online_status), so it
  // serves both the unfiltered grid and every search/skill combination.
  const {
    data: result,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['astrologersFilter', { search: debouncedSearch, skill: filter }],
    queryFn: () =>
      api.astrologers.filter({
        search: debouncedSearch.trim() || undefined,
        expertise: filter === 'all' ? undefined : [filter],
        // Homepage teaser — one row of four; the full grid lives on the
        // astrologers page. Fetch a small margin so excluding Maharaj Ji's
        // account still leaves a full row.
        per_page: 8,
      }),
    placeholderData: keepPreviousData,
  })

  const astrologers = useMemo(
    // Drop the featured Maharaj account, then hard cap at 4 even if the
    // backend ignores per_page.
    () =>
      (result?.data ?? [])
        .filter((r) => !isExcludedAstrologer(r))
        .slice(0, 4)
        .map(decorateAstrologer),
    [result],
  )
  const totalCount = result?.meta?.total ?? astrologers.length
  const onlineCount = astrologers.filter((a) => a.status === 'live').length

  useEffect(() => {
    const el = gridRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setGridInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!gridInView) return undefined
    const timers = []
    astrologers.forEach((a, i) => {
      if (revealedIds.has(a.id)) return
      const t = window.setTimeout(() => {
        setRevealedIds((prev) => new Set(prev).add(a.id))
      }, i * 70)
      timers.push(t)
    })
    return () => timers.forEach(window.clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [astrologers, gridInView])

  return (
    <section id="our-astrologers" className="relative py-28 lg:py-40 overflow-hidden" style={{ background: '#FDFAF6' }}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          className="absolute -top-32 -right-32 rounded-full bg-float-a"
          style={{ width: '520px', height: '520px', background: 'radial-gradient(circle,rgba(245,210,140,.28) 0%,transparent 70%)', filter: 'blur(70px)' }}
        />
        <div
          className="absolute -bottom-40 -left-24 rounded-full bg-float-b"
          style={{ width: '480px', height: '480px', background: 'radial-gradient(circle,rgba(220,185,120,.22) 0%,transparent 65%)', filter: 'blur(80px)' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: '700px', height: '400px', background: 'radial-gradient(ellipse,rgba(253,240,210,.5) 0%,transparent 65%)', filter: 'blur(50px)' }}
        />

        <div className="absolute bg-rot-ccw" style={{ top: '-120px', right: '8%', width: '460px', height: '460px', border: '1px solid rgba(200,146,42,.10)', borderRadius: '50%' }} />
        <div className="absolute bg-rot-cw" style={{ top: '-80px', right: '10%', width: '320px', height: '320px', border: '1px dashed rgba(200,146,42,.08)', borderRadius: '50%' }} />
        <div className="absolute bg-rot-ccw" style={{ bottom: '-100px', left: '6%', width: '380px', height: '380px', border: '1px solid rgba(200,146,42,.09)', borderRadius: '50%' }} />

        <div className="diamond-float absolute" style={{ top: '12%', left: '4%', width: '14px', height: '14px', background: 'rgba(200,146,42,.22)', transform: 'rotate(45deg)', borderRadius: '2px' }} />
        <div className="diamond-float-b absolute" style={{ top: '20%', right: '6%', width: '10px', height: '10px', background: 'rgba(200,146,42,.18)', transform: 'rotate(45deg)', borderRadius: '2px' }} />
        <div className="diamond-float absolute" style={{ bottom: '18%', left: '8%', width: '8px', height: '8px', background: 'rgba(200,146,42,.15)', transform: 'rotate(45deg)', borderRadius: '2px', animationDelay: '-2s' }} />
        <div className="diamond-float-b absolute" style={{ bottom: '25%', right: '4%', width: '12px', height: '12px', background: 'rgba(200,146,42,.20)', transform: 'rotate(45deg)', borderRadius: '2px', animationDelay: '-4s' }} />
        <div className="diamond-float absolute" style={{ top: '50%', left: '2%', width: '6px', height: '6px', background: 'rgba(200,146,42,.12)', transform: 'rotate(45deg)', borderRadius: '1px', animationDelay: '-1s' }} />

        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{ backgroundImage: 'radial-gradient(circle,rgba(200,146,42,.25) 1px,transparent 1px)', backgroundSize: '40px 40px' }}
        />

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.25),transparent)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.18),transparent)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div
          ref={headerReveal.ref}
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10 reveal${headerReveal.visible ? ' is-visible' : ''}`}
        >
          <div className="space-y-5 max-w-2xl">
            <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full" style={{ border: '1px solid rgba(200,146,42,.25)', background: 'rgba(200,146,42,.06)' }}>
              <span className="text-xs leading-none" style={{ color: '#C8922A' }}>ॐ</span>
              <span className="block w-px h-3" style={{ background: 'rgba(200,146,42,.3)' }} />
              <span className="text-[10px] uppercase font-semibold tracking-[.35em] font-royal" style={{ color: 'rgba(200,146,42,.8)' }}>
                Our Astrologers
              </span>
              <span className="block w-px h-3" style={{ background: 'rgba(200,146,42,.3)' }} />
              <span className="text-xs leading-none" style={{ color: '#C8922A' }}>✦</span>
            </div>
            <h2 className="font-royal text-3xl sm:text-5xl lg:text-[52px] font-normal tracking-[.03em] leading-[1.15]" style={{ color: '#1C140A' }}>
              Talk to Trusted
              <br />
              <span
                style={{
                  background: 'linear-gradient(90deg,#C8922A,#E5A820,#C8922A)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Expert Astrologers
              </span>
            </h2>
            <p className="text-sm sm:text-base font-light leading-relaxed max-w-xl" style={{ color: '#8B7355' }}>
              Verified Vedic experts for Kundli, career, marriage, Vastu and more — available on private video call, 24 × 7.
            </p>
          </div>
          <div className="shrink-0">
            <a
              href={appUrl("jyotish", "consultations")}
              className="book-btn-w inline-flex items-center gap-3 font-bold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 group"
              style={{ background: '#1C140A', color: '#FFF', boxShadow: '0 8px 32px -8px rgba(28,20,10,.35)' }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#C8922A'
                e.currentTarget.style.boxShadow = '0 8px 32px -8px rgba(200,146,42,.5)'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#1C140A'
                e.currentTarget.style.boxShadow = '0 8px 32px -8px rgba(28,20,10,.35)'
              }}
            >
              Meet All Astrologers
              <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>
        </div>

        <div
          ref={filterRowReveal.ref}
          className={`flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-10 reveal${filterRowReveal.visible ? ' is-visible' : ''}`}
          style={{ transitionDelay: '.04s' }}
        >
          <div className="relative w-full sm:w-64 shrink-0">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#B5A090' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
            </svg>
            <input
              id="astro-search"
              type="text"
              placeholder="Search by name…"
              className="astro-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 flex-1">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilter(tab.key)}
                className={`filter-tab-w text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full border flex-shrink-0${filter === tab.key ? ' tab-w-active' : ''
                  }`}
                style={filter === tab.key ? undefined : { borderColor: 'rgba(200,146,42,.22)', color: '#8B7355', background: '#FFF' }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <span id="astro-count" className="hidden sm:block text-[10px] font-semibold shrink-0" style={{ color: '#8B7355' }}>
            {totalCount} Astrologer{totalCount !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" id="astro-grid" ref={gridRef}>
          {isPending
            ? Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)
            : astrologers.map((astro) => (
              <AstrologerCard
                key={astro.id}
                astro={astro}
                revealed={revealedIds.has(astro.id)}
                hidden={false}
              />
            ))}
        </div>

        {isError && (
          <div className="py-16 text-center">
            <div className="text-5xl mb-4 opacity-40">🛰️</div>
            <p className="font-royal text-lg font-bold mb-1" style={{ color: '#1C140A' }}>
              Couldn’t Load Astrologers
            </p>
            <p className="text-sm mb-5" style={{ color: '#8B7355' }}>
              Something went wrong while fetching live data. Please try again.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A', background: 'rgba(200,146,42,.06)' }}
            >
              Retry
            </button>
          </div>
        )}

        {!isPending && !isError && astrologers.length === 0 && (
          <div id="astro-no-results" className="py-16 text-center">
            <div className="text-5xl mb-4 opacity-40">🔭</div>
            <p className="font-royal text-lg font-bold mb-1" style={{ color: '#1C140A' }}>
              No Astrologers Found
            </p>
            <p className="text-sm" style={{ color: '#8B7355' }}>
              Try a different name or specialty filter.
            </p>
          </div>
        )}

        <div
          ref={ctaStripReveal.ref}
          className={`mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 reveal${ctaStripReveal.visible ? ' is-visible' : ''}`}
          style={{ transitionDelay: '.1s' }}
        >
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {astrologers.slice(0, 3).map((a) => (
                <div
                  key={a.id}
                  className="w-9 h-9 rounded-full border-2 overflow-hidden flex items-center justify-center text-[10px] font-royal font-bold"
                  style={{ background: 'linear-gradient(145deg,#FEF3C7,#FDE68A)', borderColor: 'rgba(200,146,42,.25)', color: '#92400E' }}
                >
                  {a.image ? (
                    <img src={a.image} alt={a.name} className="w-full h-full object-cover" />
                  ) : (
                    a.fallbackLetter
                  )}
                </div>
              ))}
              {totalCount > 3 && (
                <div
                  className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-[9px] font-bold cursor-pointer hover:scale-105"
                  style={{ background: '#FFF', borderColor: 'rgba(139,115,85,.2)', color: '#8B7355' }}
                  onClick={() => {
                    window.location.href = appUrl("jyotish", "consultations")
                  }}
                >
                  +{totalCount - 3}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold tracking-wide" style={{ color: '#1C140A' }}>
                {totalCount} Verified Astrologer{totalCount !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#22c55e', boxShadow: '0 0 5px rgba(34,197,94,.6)' }} />
                <p className="text-[9px] uppercase tracking-wider" style={{ color: '#8B7355' }}>
                  {onlineCount} available right now
                </p>
              </div>
            </div>
          </div>

          <a
            href={appUrl("jyotish", "consultations")}
            className="inline-flex items-center gap-3 border text-[11px] uppercase tracking-[.25em] px-8 py-4 rounded-full transition-all duration-300 group font-semibold"
            style={{ borderColor: 'rgba(200,146,42,.3)', color: '#8B7355' }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = 'rgba(200,146,42,.6)'
              e.currentTarget.style.color = '#C8922A'
              e.currentTarget.style.background = 'rgba(200,146,42,.05)'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = 'rgba(200,146,42,.3)'
              e.currentTarget.style.color = '#8B7355'
              e.currentTarget.style.background = 'transparent'
            }}
          >
            Explore All Astrologers
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  )
}
